import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { PreconActions } from "@preconBlueprint/Store/PreconState";
import { DispatchProp } from "react-redux";

type HandlerType = Handler<
    {
        isSubscribed?: boolean;
        firstName?: string;
        lastName?: string;
        companyName?: string;
        jobTitle?: string;
        email?: string;
        phone?: string;
        country?: string;
        state?: string;
        address?: string;
        city?: string;
        postalCode?: string;
        quoteDetails?: string;
        manufacturer?: String;
        quantity?: number;
        productCategory?: string;
        productsOfInterest?: string;
        interestContactUs?: string;
    },
    DispatchProp<PreconActions> & {
        firstNameErrorMessage?: string;
        companyNameErrorMessage?: string;
        jobTitleErrorMessage?: string;
        emailErrorMessage?: string;
        phoneErrorMessage?: string;
        countryErrorMessage?: string;
        stateErrorMessage?: string;
        addressErrorMessage?: string;
        cityErrorMessage?: string;
        postalCodeErrorMessage?: string;
        quoteDetailsErrorMessage?: string;
        quantityErrorMessage?: string;
        productCategoryErrorMessage?: string;
        productsOfInterestErrorMessage?: string;
        manufacturerErrorMessage?: string;
    }
>;

export const ValidateFirstName: HandlerType = props => {
    const {
        parameter: { firstName },
    } = props;
    if (typeof firstName === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("First Name"));
    const errorMessage = firstName === "" ? requiredFieldMessage : "";
    props.firstNameErrorMessage = errorMessage as string;
};

export const ValidateCompanyName: HandlerType = props => {
    const {
        parameter: { companyName },
    } = props;
    if (typeof companyName === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("Company Name"));
    const errorMessage = companyName === "" ? requiredFieldMessage : "";
    props.companyNameErrorMessage = errorMessage as string;
};

export const ValidateJobTitle: HandlerType = props => {
    const {
        parameter: { jobTitle },
    } = props;
    if (typeof jobTitle === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("Job Title"));
    const errorMessage = jobTitle === "" ? requiredFieldMessage : "";
    props.jobTitleErrorMessage = errorMessage as string;
};

export const ValidateEmail: HandlerType = props => {
    const {
        parameter: { email },
    } = props;
    if (typeof email === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("Email"));
    const emailFieldMessage = siteMessage("Field_Invalid", translate("Email"));
    const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
    const errorMessage = email === "" ? requiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
    props.emailErrorMessage = errorMessage as string;
};

export const ValidatePhone: HandlerType = props => {
    const {
        parameter: { phone },
    } = props;
    if (typeof phone === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("Phone"));
    const errorMessage = phone === "" ? requiredFieldMessage : "";
    props.phoneErrorMessage = errorMessage as string;
};

export const ValidateCountry: HandlerType = props => {
    const {
        parameter: { country },
    } = props;
    if (typeof country === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("Country/Region"));
    const errorMessage = country === "" ? requiredFieldMessage : "";
    props.countryErrorMessage = errorMessage as string;
};

export const ValidateState: HandlerType = props => {
    const {
        parameter: { state },
    } = props;
    if (typeof state === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("State"));
    const errorMessage = state === "" ? requiredFieldMessage : "";
    props.stateErrorMessage = errorMessage as string;
};

export const ValidateAddress: HandlerType = props => {
    const {
        parameter: { address },
    } = props;
    if (typeof address === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("Street address"));
    const errorMessage = address === "" ? requiredFieldMessage : "";
    props.addressErrorMessage = errorMessage as string;
};

export const ValidateCity: HandlerType = props => {
    const {
        parameter: { city },
    } = props;
    if (typeof city === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("City"));
    const errorMessage = city === "" ? requiredFieldMessage : "";
    props.cityErrorMessage = errorMessage as string;
};

export const ValidatePostalCode: HandlerType = props => {
    const {
        parameter: { postalCode },
    } = props;
    if (typeof postalCode === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("Postal code"));
    const errorMessage = postalCode === "" ? requiredFieldMessage : "";
    props.postalCodeErrorMessage = errorMessage as string;
};

export const ValidateQuoteDetails: HandlerType = props => {
    const {
        parameter: { quoteDetails },
    } = props;
    if (typeof quoteDetails === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("Quote Details"));
    const errorMessage = quoteDetails === "" ? requiredFieldMessage : "";
    props.quoteDetailsErrorMessage = errorMessage as string;
};

export const ValidateProductsOfInterest: HandlerType = props => {
    const {
        parameter: { productsOfInterest },
    } = props;
    if (typeof productsOfInterest === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("Products Of Interest"));
    const errorMessage = productsOfInterest === "" ? requiredFieldMessage : "";
    props.productsOfInterestErrorMessage = errorMessage as string;
};

export const ValidateProductCategory: HandlerType = props => {
    const {
        parameter: { productCategory },
    } = props;
    if (typeof productCategory === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("Product Category"));
    const errorMessage = productCategory === "" ? requiredFieldMessage : "";
    props.productCategoryErrorMessage = errorMessage as string;
};

export const ValidateQuantity: HandlerType = props => {
    const {
        parameter: { quantity },
    } = props;
    if (typeof quantity === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("Quantity"));
    const errorMessage = quantity === 0 ? requiredFieldMessage : "";
    props.quantityErrorMessage = errorMessage as string;
};

export const ValidateManufacturer: HandlerType = props => {
    const {
        parameter: { manufacturer },
    } = props;
    if (typeof manufacturer === "undefined") {
        return;
    }

    const requiredFieldMessage = siteMessage("Field_Required", translate("Manufacturer"));
    const errorMessage = manufacturer === "" ? requiredFieldMessage : "";
    props.manufacturerErrorMessage = errorMessage as string;
};

export const DispatchValidateRfqForm: HandlerType = props => {
    props.dispatch({
        ...props.parameter,
        firstNameErrorMessage: props.firstNameErrorMessage,
        companyNameErrorMessage: props.companyNameErrorMessage,
        jobTitleErrorMessage: props.jobTitleErrorMessage,
        emailErrorMessage: props.emailErrorMessage,
        phoneErrorMessage: props.phoneErrorMessage,
        countryErrorMessage: props.countryErrorMessage,
        stateErrorMessage: props.stateErrorMessage,
        addressErrorMessage: props.addressErrorMessage,
        cityErrorMessage: props.cityErrorMessage,
        postalCodeErrorMessage: props.postalCodeErrorMessage,
        quoteDetailsErrorMessage: props.quoteDetailsErrorMessage,
        productsOfInterestErrorMessage: props.productsOfInterestErrorMessage,
        productCategoryErrorMessage: props.productCategoryErrorMessage,
        manufacturerErrorMessage: props.manufacturerErrorMessage,
        quantityErrorMessage: props.quantityErrorMessage,
        type: "Pages/Rfq/PreconValidateRfqForm",
    });
};

export const chain = [ValidateFirstName, ValidateCompanyName, ValidateJobTitle, ValidateEmail, 
    ValidatePhone, ValidateCountry, ValidateState, ValidateAddress, ValidateCity,
    ValidatePostalCode, ValidateQuoteDetails, ValidateManufacturer, ValidateQuantity, ValidateProductCategory, ValidateProductsOfInterest, DispatchValidateRfqForm];

const preconValidateRfqForm = createHandlerChainRunner(chain, "PreconValidateRfqForm");
export default preconValidateRfqForm;
